import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { ArchitectureStack } from '../components/howItWorks/ArchitectureStack';
import { LayerSection } from '../components/howItWorks/LayerSection';
import { WorkspacePreview } from '../components/howItWorks/WorkspacePreview';
import { LinkList } from '../components/layout/LinkList';
import { architectureLayers, configurableModules, exampleWorkflow, foundationDetails, rolePairs } from '../data/howItWorks';
import { solutions } from '../data/solutionCatalog';
import { easeOutStrong, fadeUp } from '../utils/motion';
const foundationIds = ['semantic-knowledge-platform', 'human-capability-framework', 'noetivra-core'] as const;
export function HowItWorksPage() {
  const layerNumber = (id: string) => architectureLayers.findIndex(layer => layer.id === id) + 1;
  const layer = (id: string) => architectureLayers[layerNumber(id) - 1];
  return <>
      {/* Overview: the whole architecture */}
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-24" data-id="element-3020" data-mp="1swo4mfhppdr5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3021" data-mp="1xuzegq80bo9d">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeOutStrong
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3022" data-mp="1xxc1viwjaqtt">
            <div className="lg:col-span-5" data-id="element-3023" data-mp="1a6pttrmj56jt">
              <p className="text-sm font-medium text-teal mb-5" data-id="element-3024" data-mp="1a3tupzyy4bts">How It Works</p>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-[3.25rem] text-ink-dark leading-[1.08] tracking-tight" data-id="element-3025" data-mp="1f4zom14ce54w">
                From connected knowledge to everyday work.
              </h1>
            </div>
            <div className="lg:col-span-7 lg:pt-10" data-id="element-3026" data-mp="rff59h4a15v3">
              <p className="text-2xl text-ink-dark leading-relaxed" data-id="element-3027" data-mp="renjsz4e8oij">
                SyNeural’s system is built in layers. Connected knowledge and proven methods sit at the foundation.
                Noetivra builds on them, bringing knowledge, human development and institutional workflows into dedicated
                workspaces.
              </p>
              <p className="mt-5 text-lg text-ink-muted leading-relaxed" data-id="element-3028" data-mp="28hzf5mri8scs">
                Organizations configure it around their needs—from coordinating citizen services and developing employees
                to exploring research and supporting clinical review.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-id="element-3029" data-mp="1atpdx9zjm8mq">
                <a href="/solutions" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3030" data-mp="1otia3ktu9h23">
                  Explore solutions <ArrowRight size={18} aria-hidden="true" data-id="element-3031" data-mp="30ai2egg6rvw4" />
                </a>
                <p className="text-sm text-ink-muted" data-id="element-3032" data-mp="2w6shc0ypzwfu">Platform under development · Interactive demos available</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.08,
          ease: easeOutStrong
        }} className="mt-16 lg:mt-20" data-id="element-3033" data-mp="38qfn2ezgtgn3">
            <ArchitectureStack data-id="element-3034" data-mp="2ytba95riexiy" />
          </motion.div>
        </div>
      </section>

      {/* Layers 1–3: SKP, HCF, Noetivra Core */}
      {foundationIds.map((id, index) => {
      const detail = foundationDetails[id];
      return <LayerSection key={id} id={id} number={layerNumber(id)} level={layer(id).level} title={layer(id).name} tone={index % 2 === 0 ? 'muted' : 'white'} intro={<>
                <p className="text-xl text-ink-dark leading-relaxed" data-id="element-3036" data-mp="e196fvmms13p">{detail.body}</p>
                <ul className="mt-6 space-y-2.5" data-id="element-3037" data-mp="e33rrb1tdv8b">
                  {detail.points.map(point => <li key={point} className="flex items-start gap-3 text-ink-dark" data-id="element-3038" data-mp="2mfz5dsfq70rj">
                      <Check size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-teal" data-id="element-3039" data-mp="2hlboaen1x8mz" />
                      {point}
                    </li>)}
                </ul>
                <a href={detail.link.href} className="mt-7 inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 sm:whitespace-nowrap" data-id="element-3040" data-mp="27mvvm78dlrbd">
                  {detail.link.label} <ArrowRight size={16} aria-hidden="true" data-id="element-3041" data-mp="2vhyl8hf07ysw" />
                </a>
              </>} data-id="element-3035" data-mp="3cs17qmi8qz7u" data-mpi={index} />;
    })}

      {/* Layer 4: Modules */}
      <LayerSection id="modules" number={layerNumber('modules')} level={layer('modules').level} title="Choose the functions your organization needs." tone="muted" intro={<p className="text-xl text-ink-dark leading-relaxed" data-id="element-3043" data-mp="3ri0cuh4xgr35">
            On top of Core, Noetivra combines reusable modules with workflows tailored to your setting. Start with a
            focused use case and extend the system as your requirements grow.
          </p>} data-id="element-3042" data-mp="3nzb1dtotk01a">
        <motion.dl {...fadeUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line rounded-lg overflow-hidden" data-id="element-3044" data-mp="9c57376ibr5l">
          {configurableModules.map(module => <div key={module.name} className="bg-white p-6 lg:p-7" data-id="element-3045" data-mp="ibs6jnfoemw0">
              <dt className="font-heading font-bold text-xl text-ink-dark" data-id="element-3046" data-mp="i9xnyfrvn6v3">{module.name}</dt>
              <dd className="mt-2 text-ink-muted leading-relaxed" data-id="element-3047" data-mp="n5cxwwmrkim8">{module.enables}</dd>
            </div>)}
        </motion.dl>
        <p className="mt-6 max-w-3xl border-l-2 border-teal pl-4 text-sm text-ink-muted leading-relaxed" data-id="element-3048" data-mp="x1q0yl4788tn">
          This describes the intended modular architecture of the platform under development. The modules are not yet
          independently installable; the interactive demos show their workflows with fictional records.
        </p>
      </LayerSection>

      {/* Layer 5: Workspaces */}
      <LayerSection id="workspaces" number={layerNumber('workspaces')} level={layer('workspaces').level} title="One environment. A workspace for each role." intro={<>
            <p className="text-xl text-ink-dark leading-relaxed" data-id="element-3050" data-mp="v1lses3lc0t6">
              People see the information and tools relevant to their work. A citizen can request support and follow an
              application. An administrator can coordinate the response. A researcher can explore findings while a
              curator reviews the knowledge behind them.
            </p>
            <p className="mt-5 text-lg text-ink-muted leading-relaxed" data-id="element-3051" data-mp="36ro0s4wpjqly">
              Related work stays connected, with access shaped around each person’s responsibilities.
            </p>
          </>} data-id="element-3049" data-mp="m2gt3jk7g3fy">
        <motion.div {...fadeUp} data-id="element-3052" data-mp="1hc3ksblhumme">
          <WorkspacePreview data-id="element-3053" data-mp="1hbu3rdiffpsu" />
        </motion.div>

        <motion.div {...fadeUp} className="mt-16" data-id="element-3054" data-mp="1man74d4020fa">
          <p className="text-sm font-medium text-ink-muted mb-3" data-id="element-3055" data-mp="1mc83875yprf9">Paired workspaces in the same configurable environment</p>
          <ul className="border-t border-line" data-id="element-3056" data-mp="1ckc2spgelp7v">
            {rolePairs.map(pair => <li key={pair.setting} className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-16 border-b border-line py-6" data-id="element-3057" data-mp="1chzeluk1yimd">
                <p className="lg:col-span-3 font-heading font-bold text-xl text-ink-dark" data-id="element-3058" data-mp="12mnx54h9exax">{pair.setting}</p>
                <div className="lg:col-span-9" data-id="element-3059" data-mp="on6el6uk4ga0">
                  <p className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-navy" data-id="element-3060" data-mp="oohir7rwzq1n">
                    <span data-id="element-3061" data-mp="tnkdnze6kfvw">{pair.workspaces[0]}</span>
                    <span aria-hidden="true" className="text-teal" data-id="element-3062" data-mp="2j04jfaxhmkpy">⇄</span>
                    <span className="sr-only" data-id="element-3063" data-mp="2nz8ts4w5eohv">and</span>
                    <span data-id="element-3064" data-mp="2nypnjlfv1ojo">{pair.workspaces[1]}</span>
                  </p>
                  <p className="mt-1.5 text-ink-muted leading-relaxed" data-id="element-3065" data-mp="1arl0qtj1ih3g">{pair.note}</p>
                </div>
              </li>)}
          </ul>
        </motion.div>
      </LayerSection>

      {/* Layer 6: Solutions, with one example workflow */}
      <section id="solutions" aria-labelledby="solutions-title" className="scroll-mt-20 bg-navy text-white py-20 lg:py-28" data-id="element-3066" data-mp="x197grapycaq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3067" data-mp="s2o8ev77dynv">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-12" data-id="element-3068" data-mp="rzs8x2fpj1xb">
            <div className="lg:col-span-5" data-id="element-3069" data-mp="n5eg97sfj8wc">
              <p className="flex items-center gap-3 text-sm font-medium text-teal mb-4" data-id="element-3070" data-mp="j2htr5zrqsn4">
                <span aria-hidden="true" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-teal/60 text-sm font-semibold" data-id="element-3071" data-mp="e35cd0ro2drx">
                  {layerNumber('solutions')}
                </span>
                {layer('solutions').level} · Example: Government
              </p>
              <h2 id="solutions-title" className="font-heading font-bold text-3xl lg:text-4xl tracking-tight leading-tight" data-id="element-3072" data-mp="2kz0fqzw4asyn">
                From a request to a practical next step.
              </h2>
            </div>
            <p className="lg:col-span-7 lg:pt-11 text-lg text-white/80 leading-relaxed" data-id="element-3073" data-mp="2upblmrzd3c86">
              A solution brings every layer together for one field. Here is one example from the Government solution;
              research, clinical and other solutions follow workflows suited to their own work.
            </p>
          </motion.div>

          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15 border border-white/15 rounded-lg overflow-hidden" data-id="element-3074" data-mp="26xxu2o91bko9">
            {exampleWorkflow.map((item, index) => <motion.li key={item.step} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.24),
            ease: easeOutStrong
          }} className="bg-navy p-6 lg:p-8" data-id="element-3075" data-mp="3iduylktcwd6l" data-mpi={index}>
                <h3 className="flex items-center gap-3 font-heading font-bold text-xl leading-snug" data-id="element-3076" data-mp="3um7pj3x6sajk">
                  <span aria-hidden="true" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/40 text-sm font-semibold text-white" data-id="element-3077" data-mp="3dagx85acua0">
                    {index + 1}
                  </span>
                  {item.step}
                </h3>
                <p className="mt-3 text-white/75 leading-relaxed" data-id="element-3078" data-mp="3gti533xb3dok">{item.body}</p>
              </motion.li>)}
          </ol>

          <motion.a {...fadeUp} href="/government" className="mt-10 inline-flex items-center gap-2 font-semibold text-white hover:text-teal transition-colors duration-150 whitespace-nowrap" data-id="element-3079" data-mp="r34qngub62q1">
            See the Government solution <ArrowRight size={16} aria-hidden="true" data-id="element-3080" data-mp="2bmgvs8ycxzo8" />
          </motion.a>
        </div>
      </section>

      {/* Next step */}
      <section aria-labelledby="field-title" className="bg-surface-bg py-20 lg:py-28" data-id="element-3081" data-mp="26ugplyrp2aj7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3082" data-mp="21wx9sufclpp5">
          <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10" data-id="element-3083" data-mp="21ukuyq4vzvgb">
            <h2 id="field-title" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-3084" data-mp="1svz487p9ha4g">
              See Noetivra in your field.
            </h2>
            <a href="/solutions" className="inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-3085" data-mp="1ixjh0ftcul08">
              All solutions <ArrowRight size={16} aria-hidden="true" data-id="element-3086" data-mp="3dbawvx2a2144" />
            </a>
          </motion.div>
          <LinkList label="Solutions" columns={2} items={solutions.map(solution => ({
          title: solution.pageTitle,
          description: solution.summary,
          href: solution.href
        }))} data-id="element-3087" data-mp="1bm505247cr1r" />
        </div>
      </section>
    </>;
}